import { memo, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { evaluateFormula } from '@/components/forms/formula-evaluator';
import type { FormFieldBaseProps } from './field-base';

export interface ComputedFieldProps extends FormFieldBaseProps<number | null | undefined> {
  /** Sibling values in the same form; the formula references these by key. */
  values: Record<string, unknown>;
}

/**
 * Read-only. Evaluates `field.formula` against numeric-coerced sibling values.
 * onChange is not invoked — computed fields do not write into the form's value
 * map on render (values are recalculated on display / persistence boundaries).
 */
export const ComputedField = memo(function ComputedField({
  field,
  values,
  readOnly: _readOnly = true,
}: ComputedFieldProps) {
  const { theme } = useTheme();

  const numericValues = useMemo(() => {
    const out: Record<string, number> = {};
    for (const key of Object.keys(values)) {
      const raw = values[key];
      if (raw == null || raw === '') continue;
      const n = Number(raw as number | string);
      if (!Number.isNaN(n)) {
        out[key] = n;
      }
    }
    return out;
  }, [values]);

  const result = useMemo(() => {
    const formula = typeof field.formula === 'string' ? field.formula : '';
    if (!formula) return null;
    try {
      return evaluateFormula(formula, numericValues);
    } catch {
      return null;
    }
  }, [field.formula, numericValues]);

  const display = useMemo(() => {
    if (result == null || Number.isNaN(result)) return '';
    if (field.format === 'currency') {
      return result.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    }
    return String(result);
  }, [result, field.format]);

  return (
    <div className={'form-group computed-field' + (theme ? ' ' + theme : '')}>
      <label className={'field-label' + (field.required ? ' required' : '')}>
        {field.label}
      </label>
      <output className="kstc-input form-input computed-field-output" htmlFor={field.key}>
        {display}
      </output>
    </div>
  );
});
